import React from 'react';
import { Box, Button, LinearProgress, Tooltip, Typography } from '@mui/material';
import { BiPause, BiSolidInfoCircle } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';

import type { PortalProductEntry } from '@features/products/types';
import { usePortalHost } from '@hopdrive/feature-portal-products';

import { formatCurrency } from '../utils/guestRideUtils';
import {
  formatSettlementDate,
  isCreditBlocked,
  overLimitBy,
  readGuestRidesCredit,
  type CreditActionView,
  type CreditMeterView,
} from './guestRidesCredit';

// The Guest Rides spend meter: what the rooftop has spent and its limit as two large figures
// bracketing one bar, with the payment action and the billing date underneath. Every number
// comes from readGuestRidesCredit, which reads the product decision; nothing here computes a
// limit, a threshold or a remaining balance. A field the decision omits renders nothing.
//
// While new rides are paused for a credit reason (over the limit, a failed payment, no payment
// method on auto-pay), the meter moves into a dark status panel that carries the reason and the
// recovery actions, and the Guest Rides layout turns the shell's generic state banner off so the
// dealer sees one account-state surface. Any other blocked reason keeps the shell's banner, with
// the light meter beneath it.

const LIMIT_EXPLAINER =
  'HopDrive sets this limit for your rooftop. When your unpaid guest rides balance reaches it, new rides pause until a payment clears. Rides already booked are not affected.';

const SEPARATOR = ' · ';

interface SpendMeterProps {
  meter: CreditMeterView;
  blocked: boolean;
  settlementDate: string | null;
  payRoute: string | undefined;
}

const SpendMeter: React.FC<SpendMeterProps> = ({ meter, blocked, settlementDate, payRoute }) => {
  const navigate = useNavigate();
  const percent = Math.round(meter.usedFraction * 100);

  const figureSx = { fontSize: { xs: '1.75rem', sm: '2.25rem' }, fontWeight: 800, lineHeight: 1.15 } as const;

  return (
    <Box data-testid='guest-rides-credit-meter' sx={{ pt: 1, pb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
        <Box>
          <Typography variant='body2' color='text.secondary'>
            Spent this billing cycle
          </Typography>
          <Typography data-testid='guest-rides-credit-spent' sx={figureSx}>
            {formatCurrency(meter.outstandingBalance)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Tooltip title={LIMIT_EXPLAINER} placement='top' arrow>
            <Typography
              variant='body2'
              color='secondary'
              tabIndex={0}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'help' }}
            >
              <BiSolidInfoCircle aria-hidden size={20} />
              Spend limit
            </Typography>
          </Tooltip>
          <Typography data-testid='guest-rides-credit-limit' sx={figureSx}>
            {formatCurrency(meter.creditLimit)}
          </Typography>
        </Box>
      </Box>

      {/* Neutral while rides can still be requested, red once the decision has paused them.
          The tone follows the decision's state, never a comparison of the two figures here. */}
      <LinearProgress
        variant='determinate'
        value={percent}
        color={blocked ? 'error' : 'secondary'}
        aria-label='Guest rides spend limit used'
        sx={{ mt: 1.5, height: 10, borderRadius: 5 }}
      />

      <Box
        sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}
      >
        <Box>
          {/* While blocked, the shell's state banner already carries Pay now, so the meter does
              not offer a second payment button. */}
          {!blocked && payRoute !== undefined && (
            <Button variant='outlined' color='secondary' onClick={() => navigate(payRoute)}>
              Make Payment
            </Button>
          )}
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'right' }}>
          {meter.remainingCredit !== null && (
            <>
              <Box component='strong' sx={{ color: 'text.primary' }}>
                {formatCurrency(meter.remainingCredit)} remaining
              </Box>
              {SEPARATOR}
            </>
          )}
          {blocked ? 'new rides are paused' : 'new rides pause at limit'}
          {settlementDate !== null && (
            <>
              {SEPARATOR}Next autopay{' '}
              <Box component='strong' sx={{ color: 'text.primary' }}>
                {settlementDate}
              </Box>
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

/** The blocked panel's line under its title, per credit reason. */
function blockedBody(reasonKey: string | undefined, meter: CreditMeterView | null): string {
  if (reasonKey === 'payment_failed') {
    return "Your most recent payment didn't go through. Pay the balance or update your payment method to resume.";
  }
  if (reasonKey === 'missing_payment_method') {
    return "Automatic billing is on, but there's no payment method on file. Add one to resume.";
  }
  const over = overLimitBy(meter);
  return over === null
    ? "You've reached your spend limit. Pay the balance to resume."
    : `You're ${formatCurrency(over)} over your spend limit. Pay the balance to resume.`;
}

interface BlockedPanelProps {
  meter: CreditMeterView | null;
  body: string;
  actions: CreditActionView[];
  settlementDate: string | null;
}

/** The account-state panel for a credit block: reason and recovery actions on top, the meter in red below. */
const BlockedPanel: React.FC<BlockedPanelProps> = ({ meter, body, actions, settlementDate }) => {
  const navigate = useNavigate();
  // Where the limit falls along a bar that is full with the balance. Only drawn past the limit,
  // since at exactly the limit the tick would sit on the bar's end.
  const limitAt = meter !== null && meter.outstandingBalance > meter.creditLimit ? meter.creditLimit / meter.outstandingBalance : null;
  const figureSx = { fontSize: { xs: '1.75rem', sm: '2.25rem' }, fontWeight: 800, lineHeight: 1.15, color: 'common.white' } as const;

  return (
    <Box
      data-testid='guest-rides-credit-blocked'
      sx={{ bgcolor: 'grey.900', color: 'common.white', borderRadius: 3, p: { xs: 2.5, sm: 3 }, mb: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box
          aria-hidden
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BiPause size={28} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3 }}>New rides are paused</Typography>
          <Typography variant='body2' sx={{ color: 'grey.300', mt: 0.25 }}>
            {body}
          </Typography>
        </Box>
        {actions.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Pay Now is the primary path; Update Payment Method sits beside it as a text button.
                Both are the portal's existing flows, resolved through the host's route map. */}
            {actions
              .filter(action => action.type !== 'pay_now')
              .map(action => (
                <Button key={action.type} color='inherit' onClick={() => navigate(action.route)} sx={{ fontWeight: 600 }}>
                  {action.type === 'update_payment_method' ? 'Update Payment Method' : action.label}
                </Button>
              ))}
            {actions
              .filter(action => action.type === 'pay_now')
              .map(action => (
                <Button key={action.type} variant='contained' color='primary' onClick={() => navigate(action.route)}>
                  Pay Now
                </Button>
              ))}
          </Box>
        )}
      </Box>

      {meter !== null && (
        <Box data-testid='guest-rides-credit-meter' sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
            <Box>
              <Typography variant='body2' sx={{ color: 'grey.300' }}>
                Spent this billing cycle
              </Typography>
              <Typography data-testid='guest-rides-credit-spent' sx={figureSx}>
                {formatCurrency(meter.outstandingBalance)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant='body2' sx={{ color: 'grey.300' }}>
                Spend limit
              </Typography>
              <Typography data-testid='guest-rides-credit-limit' sx={figureSx}>
                {formatCurrency(meter.creditLimit)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ position: 'relative', mt: 2 }}>
            <LinearProgress
              variant='determinate'
              value={Math.round(meter.usedFraction * 100)}
              color='error'
              aria-label='Guest rides spend limit used'
              sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255, 255, 255, 0.15)' }}
            />
            {limitAt !== null && (
              <Box
                aria-hidden
                data-testid='guest-rides-credit-limit-marker'
                style={{ left: `${limitAt * 100}%` }}
                sx={{
                  position: 'absolute',
                  top: -6,
                  bottom: -6,
                  width: '2px',
                  borderRadius: 1,
                  bgcolor: 'common.white',
                }}
              />
            )}
          </Box>

          {settlementDate !== null && (
            <Typography variant='body2' sx={{ color: 'grey.300', mt: 1.5, textAlign: 'right' }}>
              Next autopay{' '}
              <Box component='strong' sx={{ color: 'common.white' }}>
                {settlementDate}
              </Box>
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

interface GuestRidesCreditPanelProps {
  entry: PortalProductEntry | undefined;
}

/**
 * Mounted by the Guest Rides layout in the shell's banner area, so it stays put across the
 * product's tabs. A credit block renders the dark status panel; otherwise the light meter, or
 * nothing when the decision carries no limit.
 */
const GuestRidesCreditPanel: React.FC<GuestRidesCreditPanelProps> = ({ entry }) => {
  // Recovery actions resolve against the host's routes; with none (the embed), they drop out
  // rather than rendering buttons that go nowhere.
  const { routing } = usePortalHost();
  const view = readGuestRidesCredit(entry, routing.actionRoutes);
  const settlementDate = formatSettlementDate(entry?.context?.nextSettlementDate, 'short');

  if (isCreditBlocked(entry)) {
    return (
      <BlockedPanel
        meter={view.meter}
        body={blockedBody(entry?.reason?.key, view.meter)}
        actions={view.notice?.actions ?? []}
        settlementDate={settlementDate}
      />
    );
  }

  if (view.meter === null) return null;

  const blocked = entry?.state === 'limited' || entry?.state === 'read_only';
  return (
    <SpendMeter meter={view.meter} blocked={blocked} settlementDate={settlementDate} payRoute={routing.actionRoutes.pay_now} />
  );
};

export default GuestRidesCreditPanel;
